"use client";

import { useEffect, useRef, memo } from "react";

interface ConfettiProps {
  isActive: boolean;
  colors?: string[];
  particleCount?: number;
  className?: string;
  mode?: "fall" | "burst";
}

// Brand colors
const DEFAULT_COLORS = [
  "#5E3A3A",
  "#7A4F4F",
  "#966565",
  "#B27B7B",
  "#C47979",
  "#D4A5A5",
  "#ffffff",
];

interface Particle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  color: string;
  size: number;
  rotation: number;
  rotationSpeed: number;
  opacity: number;
  shape: "circle" | "square" | "triangle";
}

export const Confetti = memo(function Confetti({
  isActive,
  colors = DEFAULT_COLORS,
  particleCount = 100,
  className = "",
  mode = "burst",
}: ConfettiProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const particlesRef = useRef<Particle[]>([]);
  const animationRef = useRef<number>(0);

  useEffect(() => {
    if (!isActive) {
      particlesRef.current = [];
      return;
    }

    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    // Set canvas size
    const resize = () => {
      canvas.width = canvas.offsetWidth * window.devicePixelRatio;
      canvas.height = canvas.offsetHeight * window.devicePixelRatio;
      ctx.scale(window.devicePixelRatio, window.devicePixelRatio);
    };
    resize();
    window.addEventListener("resize", resize);

    // Create particles
    const shapes: Array<"circle" | "square" | "triangle"> = [
      "circle",
      "square",
      "triangle",
    ];
    const centerX = canvas.offsetWidth / 2;
    const centerY = canvas.offsetHeight / 2;

    particlesRef.current = Array.from({ length: particleCount }, (_, i) => {
      if (mode === "burst") {
        // Gentle spiral outward like snow - clockwise direction
        const angle = (i / particleCount) * Math.PI * 2; // evenly distributed
        const velocity = Math.random() * 2 + 1; // slower
        return {
          x: centerX,
          y: centerY,
          vx: Math.cos(angle) * velocity,
          vy: Math.sin(angle) * velocity,
          color: colors[Math.floor(Math.random() * colors.length)],
          size: Math.random() * 8 + 4,
          rotation: Math.random() * 360,
          rotationSpeed: (Math.random() - 0.5) * 5, // slower rotation
          opacity: 1,
          shape: shapes[Math.floor(Math.random() * shapes.length)],
          angle: angle, // store for spiral movement
        } as Particle & { angle: number };
      } else {
        // Fall from top
        return {
          x: Math.random() * canvas.offsetWidth,
          y: -20 - Math.random() * 100,
          vx: (Math.random() - 0.5) * 4,
          vy: Math.random() * 3 + 2,
          color: colors[Math.floor(Math.random() * colors.length)],
          size: Math.random() * 8 + 4,
          rotation: Math.random() * 360,
          rotationSpeed: (Math.random() - 0.5) * 10,
          opacity: 1,
          shape: shapes[Math.floor(Math.random() * shapes.length)],
        };
      }
    });

    let time = 0;
    const animate = () => {
      ctx.clearRect(0, 0, canvas.offsetWidth, canvas.offsetHeight);
      time += 0.02;

      let activeParticles = 0;

      particlesRef.current.forEach((p) => {
        if (p.opacity <= 0) return;
        activeParticles++;

        if (mode === "burst") {
          // Spiral outward clockwise - starts slow, speeds up in middle
          const pWithAngle = p as Particle & { angle?: number };
          if (pWithAngle.angle !== undefined) {
            pWithAngle.angle += 0.02; // clockwise rotation
          }

          // Speed multiplier: starts at 0.3, peaks at 1.5 around t=1.2, then stays
          const speedMultiplier = Math.min(0.3 + time * 1.0, 1.5);

          // Outward drift with spiral - accelerates over time
          p.x += p.vx * speedMultiplier;
          p.y += p.vy * speedMultiplier + 0.2;
          p.vx += Math.cos(time + (pWithAngle.angle || 0)) * 0.03;
          p.vy *= 0.998;
          p.rotation += p.rotationSpeed * speedMultiplier;

          // Fade out smoothly based on distance
          const dist = Math.sqrt(
            Math.pow(p.x - centerX, 2) + Math.pow(p.y - centerY, 2),
          );
          const maxDist =
            Math.min(canvas.offsetWidth, canvas.offsetHeight) * 0.4;
          if (dist > maxDist * 0.5) {
            p.opacity -= 0.01;
          }
          // Quick cleanup after time limit
          if (time > 2.5) {
            p.opacity = 0;
          }
          // Ensure opacity doesn't go below 0
          if (p.opacity < 0.01) {
            p.opacity = 0;
          }
        } else {
          // Update position for fall mode
          p.x += p.vx;
          p.y += p.vy;
          p.vy += 0.15;
          p.vx *= 0.98;
          p.rotation += p.rotationSpeed;

          if (p.y > canvas.offsetHeight * 0.7) {
            p.opacity -= 0.02;
          }
          // Ensure opacity doesn't go below 0
          if (p.opacity < 0.01) {
            p.opacity = 0;
          }
        }

        // Draw particle
        ctx.save();
        ctx.translate(p.x, p.y);
        ctx.rotate((p.rotation * Math.PI) / 180);
        ctx.globalAlpha = p.opacity;
        ctx.fillStyle = p.color;

        if (p.shape === "circle") {
          ctx.beginPath();
          ctx.arc(0, 0, p.size / 2, 0, Math.PI * 2);
          ctx.fill();
        } else if (p.shape === "square") {
          ctx.fillRect(-p.size / 2, -p.size / 2, p.size, p.size);
        } else {
          ctx.beginPath();
          ctx.moveTo(0, -p.size / 2);
          ctx.lineTo(p.size / 2, p.size / 2);
          ctx.lineTo(-p.size / 2, p.size / 2);
          ctx.closePath();
          ctx.fill();
        }

        ctx.restore();
      });

      if (activeParticles > 0) {
        animationRef.current = requestAnimationFrame(animate);
      } else {
        // Clear canvas completely when done
        ctx.clearRect(0, 0, canvas.offsetWidth, canvas.offsetHeight);
        particlesRef.current = [];
      }
    };

    animationRef.current = requestAnimationFrame(animate);

    return () => {
      window.removeEventListener("resize", resize);
      cancelAnimationFrame(animationRef.current);
      // Clear canvas on unmount
      if (ctx) {
        ctx.clearRect(0, 0, canvas.offsetWidth, canvas.offsetHeight);
      }
      particlesRef.current = [];
    };
  }, [isActive, colors, particleCount, mode]);

  if (!isActive) return null;

  return (
    <canvas
      ref={canvasRef}
      className={`absolute inset-0 w-full h-full pointer-events-none z-20 ${className}`}
    />
  );
});
